import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, created, transcribed, evidence_added, analyzed

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/claims`);
      setClaims(response.data);
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClaims = filter === 'all' 
    ? claims 
    : claims.filter(c => c.status === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
      case 'transcribed': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50';
      case 'evidence_added': return 'bg-green-500/20 text-green-300 border-green-500/50';
      case 'analyzed': return 'bg-purple-500/20 text-purple-300 border-purple-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  const getNextAction = (status) => {
    switch (status) {
      case 'created': return 'Add transcription';
      case 'transcribed': return 'Add evidence';
      case 'evidence_added': return 'Generate report';
      case 'analyzed': return 'View report';
      default: return 'Continue';
    }
  };

  const handleClaimClick = (claim) => {
    if (claim.status === 'created' || claim.status === 'transcribed') {
      navigate(`/luluverify?claim_id=${claim.id}`);
    } else if (claim.status === 'evidence_added' || claim.status === 'analyzed') {
      navigate(`/intelligence?claim_id=${claim.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              ← Home
            </button>
            <div className="flex items-center space-x-3">
              <img src="/veritascore-logo.png" alt="VeritasCore" className="h-10 w-10" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">VERITASCORE.IO</h1>
                <p className="text-xs text-gray-400">Dashboard — All Claims</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/claimvoice')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold transition-all"
            data-testid="new-claim-btn"
          >
            + New Claim
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="text-3xl font-bold text-white mb-2">{claims.length}</div>
            <div className="text-gray-400">Total Claims</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="text-3xl font-bold text-blue-400 mb-2">
              {claims.filter(c => c.status === 'created').length}
            </div>
            <div className="text-gray-400">Created</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="text-3xl font-bold text-green-400 mb-2">
              {claims.filter(c => c.status === 'evidence_added').length}
            </div>
            <div className="text-gray-400">With Evidence</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="text-3xl font-bold text-purple-400 mb-2">
              {claims.filter(c => c.status === 'analyzed').length}
            </div>
            <div className="text-gray-400">Analyzed</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex space-x-3">
          {['all', 'created', 'transcribed', 'evidence_added', 'analyzed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === f 
                  ? 'bg-white/20 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`filter-${f}-btn`}
            >
              {f.replace('_', ' ').toUpperCase()}
            </button>
          ))}
        </div>

        {/* Claims List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-white/50"></div>
            <p className="text-white mt-4">Loading claims...</p>
          </div>
        ) : filteredClaims.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-2xl border border-white/10">
            <p className="text-gray-400 text-lg">No claims found</p>
            <button
              onClick={() => navigate('/claimvoice')}
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
            >
              Create First Claim
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredClaims.map(claim => (
              <div
                key={claim.id}
                onClick={() => handleClaimClick(claim)}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all cursor-pointer"
                data-testid={`claim-${claim.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-white">{claim.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(claim.status)}`}>
                        {claim.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-gray-400 mb-2">{claim.incident_type.replace('_', ' ')}</p>
                    <p className="text-gray-500 text-sm">
                      Created: {new Date(claim.created_at).toLocaleDateString()}
                    </p>
                    {claim.raw_transcript && (
                      <p className="text-gray-300 mt-3 line-clamp-2">
                        {claim.raw_transcript.substring(0, 150)}...
                      </p>
                    )}
                  </div>
                  <div className="ml-4">
                    <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-semibold transition-all">
                      {getNextAction(claim.status)} →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}